import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import DateInput, { DATE_INPUT_FIELD_CLASS } from './DateInput'
import { ChevronRightIcon, FormCard, PrimaryButton, SecondaryButton, YesNoToggle } from './ui'
import type { Driver, PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import {
  isDdMmYyyyInLocalRange,
  isValidDdMmYyyyCalendarDate,
  parseDdMmYyyyToDate,
  PORTAL_DATE_MIN,
  startOfTodayLocal,
} from '../utils/dobDateInput'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimIncidentHref,
  buildClaimOtherGstHref,
  buildClaimOtherMultiVehicleOtherPartyHref,
  buildClaimOtherSingleVehicleCollisionWithHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import { CLAIM_FORM_CONTROL_CLASS, CLAIM_FORM_SELECT_CLASS } from '../claims/claimFormFieldClasses'
import { policyPrimaryDriverRecord, policySecondaryListedDrivers } from '../claims/multiVehiclePolicyDrivers'
import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  isOtherClaimDriverCaptureGateSatisfied,
  multiVehicleDriverIdentityResetPatch,
  persistMultiVehicleCollisionPatch,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useEnsureOtherBranchGstAnswered } from '../claims/useEnsureOtherBranchGstAnswered'
import {
  claimFormFieldClass,
  CLAIM_FORM_FIELD_ERROR_RING_CLASS,
  CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'

const MULTI_VEHICLE_SUBTYPE = 'multi-vehicle-collision' as const satisfies OtherClaimSubtypeId
const SINGLE_VEHICLE_SUBTYPE = 'single-vehicle' as const satisfies OtherClaimSubtypeId

function isDriverStepSubtype(id: OtherClaimSubtypeId | null): id is typeof MULTI_VEHICLE_SUBTYPE | typeof SINGLE_VEHICLE_SUBTYPE {
  return id === MULTI_VEHICLE_SUBTYPE || id === SINGLE_VEHICLE_SUBTYPE
}

function isDriverDetailsGateSatisfiedForSubtype(
  draft: ReturnType<typeof readClaimDraft>,
  subtype: typeof MULTI_VEHICLE_SUBTYPE | typeof SINGLE_VEHICLE_SUBTYPE
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== subtype) return false
  return isOtherClaimDriverCaptureGateSatisfied(draft)
}

type Phase = 'questions' | 'driverDetails'

function primaryDriverDisplayName(primary: Driver | null): string {
  if (!primary) return 'the main driver on your policy'
  const n = [primary.firstName?.trim(), primary.lastName?.trim()].filter(Boolean).join(' ')
  return n || 'the main driver on your policy'
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function isValidDobField(s: string): boolean {
  const t = s.trim()
  return isDdMmYyyyInLocalRange(t, PORTAL_DATE_MIN, startOfTodayLocal())
}

function isValidExpiryField(s: string): boolean {
  const raw = s.trim()
  if (!isValidDdMmYyyyCalendarDate(raw)) return false
  const d = parseDdMmYyyyToDate(raw)
  if (!d) return false
  return startOfLocalDay(d) >= startOfLocalDay(startOfTodayLocal())
}

function isValidYearsLicensedInput(s: string): boolean {
  const yearsStr = s.trim()
  if (!/^\d+$/.test(yearsStr)) return false
  const yearsParsed = parseInt(yearsStr, 10)
  return Number.isFinite(yearsParsed) && Number.isInteger(yearsParsed) && yearsParsed >= 1 && yearsParsed <= 100
}

function isDriverDetailsFormComplete(fields: {
  firstName: string
  lastName: string
  dob: string
  licenseNumber: string
  licenseExpiry: string
  yearsLicensedInput: string
}): boolean {
  return (
    Boolean(fields.firstName.trim()) &&
    Boolean(fields.lastName.trim()) &&
    isValidDobField(fields.dob) &&
    Boolean(fields.licenseNumber.trim()) &&
    isValidExpiryField(fields.licenseExpiry) &&
    isValidYearsLicensedInput(fields.yearsLicensedInput)
  )
}

/** Upper bound for licence expiry calendar (today + 80 years, local). */
function licenceExpiryMaxDate(): Date {
  const t = startOfTodayLocal()
  return new Date(t.getFullYear() + 80, 11, 31)
}

function initialPhase(draft: ReturnType<typeof readClaimDraft>, secondaryCount: number): Phase {
  if (!draft) return 'questions'
  if (draft.multiVehiclePrimaryDriverDriving === null || draft.multiVehiclePrimaryDriverDriving === undefined) {
    return 'questions'
  }
  if (draft.multiVehiclePrimaryDriverDriving === true) return 'driverDetails'
  const s = draft.multiVehicleSecondaryOnPolicyDriving
  if (secondaryCount > 0) {
    if (s === undefined || s === null) return 'questions'
    if (s === true && !draft.multiVehicleListedDriverId?.trim()) return 'questions'
  } else if (s !== false) {
    return 'questions'
  }
  return 'driverDetails'
}

/**
 * Multi-vehicle collision only: who was driving and licence details (replaces towing for this subtype).
 * Primary-name and listed-driver pattern (dropdown + “None of the above”) before driver details.
 */
export default function ClaimOtherMultiVehicleDriverStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const primaryHeadingId = useId()
  const whoWasDrivingHeadingId = useId()
  const listedSelectId = useId()
  const detailsHeadingId = useId()
  const dobId = useId()
  const licenseExpiryId = useId()
  const driverDetailsSummaryId = useId()
  const driverNamesErrorId = useId()
  const driverDobErrorId = useId()
  const driverLicenceErrorId = useId()
  const driverExpiryErrorId = useId()
  const driverYearsErrorId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as {
    policy?: PolicyLink
    preserveScroll?: boolean
  } | null

  const policy = navState?.policy ?? null
  const primaryDriver = useMemo(() => (policy ? policyPrimaryDriverRecord(policy) : null), [policy])
  const secondaryDrivers = useMemo(() => (policy ? policySecondaryListedDrivers(policy) : []), [policy])
  const primaryName = useMemo(() => primaryDriverDisplayName(primaryDriver), [primaryDriver])

  useEnsureOtherBranchGstAnswered({
    claimEntryBranch: 'other',
    policyRef,
    policyTypeQuery: typeInQuery,
    otherClaimSubtype: claimSubtype,
    navPolicy: navState?.policy,
  })

  const [phase, setPhase] = useState<Phase>(() =>
    initialPhase(readClaimDraft(), secondaryDrivers.length)
  )

  const [primaryAnswer, setPrimaryAnswer] = useState<'Yes' | 'No' | null>(() => {
    const a = readClaimDraft()?.multiVehiclePrimaryDriverDriving
    if (a === true) return 'Yes'
    if (a === false) return 'No'
    return null
  })
  const [listedId, setListedId] = useState<string | null>(() => readClaimDraft()?.multiVehicleListedDriverId ?? null)

  const [firstName, setFirstName] = useState(() => readClaimDraft()?.multiVehicleDriverFirstName ?? '')
  const [lastName, setLastName] = useState(() => readClaimDraft()?.multiVehicleDriverLastName ?? '')
  const [dob, setDob] = useState(() => readClaimDraft()?.multiVehicleDriverDobDdMmYyyy ?? '')
  const [gender, setGender] = useState<'Male' | 'Female' | ''>(() => {
    const g = readClaimDraft()?.multiVehicleDriverGender
    if (g === 'Male' || g === 'Female') return g
    return ''
  })
  const [licenseNumber, setLicenseNumber] = useState(() => readClaimDraft()?.multiVehicleLicenseNumber ?? '')
  const [licenseExpiry, setLicenseExpiry] = useState(() => readClaimDraft()?.multiVehicleLicenseExpiryDdMmYyyy ?? '')
  const [yearsLicensedInput, setYearsLicensedInput] = useState(() => {
    const y = readClaimDraft()?.multiVehicleYearsLicensed
    return typeof y === 'number' && Number.isInteger(y) && y >= 1 && y <= 100 ? String(y) : ''
  })

  const [formError, setFormError] = useState<string | null>(null)
  const prefillRef = useRef<string | null>(null)

  const navigateWithPolicy = useCallback(
    (path: string) => {
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    },
    [navigate, navState?.policy]
  )

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isDriverStepSubtype(claimSubtype)) {
      const path = buildClaimOtherGstHref(policyRef, typeInQuery, claimSubtype)
      navigateWithPolicy(path)
    }
  }, [claimSubtype, navigateWithPolicy, policyRef, typeInQuery])

  const applyDriverPrefill = useCallback((source: Driver | null, options?: { force?: boolean }) => {
    const force = options?.force === true
    setFormError(null)
    if (source) {
      const fn = (source.firstName ?? '').trim()
      const ln = (source.lastName ?? '').trim()
      const dobSrc = source.dateOfBirth?.trim() ?? ''
      const g =
        source.gender === 'Male' || source.gender === 'Female' ? source.gender : ('' as const)
      setFirstName((prev) => (force || !prev.trim() ? fn : prev))
      setLastName((prev) => (force || !prev.trim() ? ln : prev))
      setDob((prev) => (force || !prev.trim() ? dobSrc : prev))
      setGender((prev) => (force || !(prev === 'Male' || prev === 'Female') ? g : prev))
    } else if (force) {
      setFirstName('')
      setLastName('')
      setDob('')
      setGender('')
    } else {
      setFirstName((prev) => (prev.trim() ? prev : ''))
      setLastName((prev) => (prev.trim() ? prev : ''))
      setDob((prev) => (prev.trim() ? prev : ''))
      setGender((prev) => (prev === 'Male' || prev === 'Female' ? prev : ''))
    }
  }, [])

  useEffect(() => {
    if (phase !== 'driverDetails') {
      prefillRef.current = null
      return
    }
    const d = readClaimDraft()
    const key = `${d?.multiVehiclePrimaryDriverDriving}-${d?.multiVehicleSecondaryOnPolicyDriving}-${d?.multiVehicleListedDriverId}`
    if (prefillRef.current === key) return
    prefillRef.current = key

    if (d?.multiVehiclePrimaryDriverDriving === true && primaryDriver) {
      applyDriverPrefill(primaryDriver, { force: true })
      return
    }
    if (d?.multiVehicleSecondaryOnPolicyDriving === true && d.multiVehicleListedDriverId) {
      const sel = secondaryDrivers.find((x) => x.id === d.multiVehicleListedDriverId)
      applyDriverPrefill(sel ?? null, { force: true })
      return
    }
    const fn = d?.multiVehicleDriverFirstName?.trim() ?? ''
    const ln = d?.multiVehicleDriverLastName?.trim() ?? ''
    setFirstName(fn)
    setLastName(ln)
    setDob(d?.multiVehicleDriverDobDdMmYyyy?.trim() ?? '')
    setGender(
      d?.multiVehicleDriverGender === 'Male' || d?.multiVehicleDriverGender === 'Female'
        ? d.multiVehicleDriverGender
        : ''
    )
    setLicenseNumber(d?.multiVehicleLicenseNumber?.trim() ?? '')
    setLicenseExpiry(d?.multiVehicleLicenseExpiryDdMmYyyy?.trim() ?? '')
    setYearsLicensedInput(
      typeof d?.multiVehicleYearsLicensed === 'number' ? String(d.multiVehicleYearsLicensed) : ''
    )
    setFormError(null)
  }, [phase, primaryDriver, secondaryDrivers, applyDriverPrefill])

  const draft = readClaimDraft()
  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )
  const mvRailSummary = formatMultiVehicleCollisionRailSummary(draft ?? undefined)

  const backToIncident = () => {
    if (claimSubtype === null) return
    const path = buildClaimIncidentHref(policyRef, typeInQuery, 'other', claimSubtype)
    navigateWithPolicy(path)
  }

  const handleHeaderBack = () => {
    if (phase === 'questions') {
      backToIncident()
      return
    }
    if (phase === 'driverDetails') {
      const d = readClaimDraft()
      if (d?.multiVehiclePrimaryDriverDriving === true) {
        setPhase('questions')
        setPrimaryAnswer(null)
        persistMultiVehicleCollisionPatch(policyRef, {
          multiVehiclePrimaryDriverDriving: null,
          multiVehicleDriverCaptureMode: null,
        })
        return
      }
      if (d?.multiVehicleSecondaryOnPolicyDriving === true) {
        setPhase('questions')
        setListedId(null)
        persistMultiVehicleCollisionPatch(policyRef, {
          multiVehicleSecondaryOnPolicyDriving: null,
          multiVehicleListedDriverId: null,
          multiVehicleDriverCaptureMode: null,
        })
        return
      }
      setPhase('questions')
      setPrimaryAnswer(null)
      setListedId(null)
      persistMultiVehicleCollisionPatch(policyRef, {
        multiVehiclePrimaryDriverDriving: null,
        multiVehicleSecondaryOnPolicyDriving: null,
        multiVehicleListedDriverId: null,
        multiVehicleDriverCaptureMode: null,
      })
    }
  }

  const persistPrimaryAnswer = (v: 'Yes' | 'No') => {
    clearQuestionsSubmitAttempt()
    setPrimaryAnswer(v)
    setListedId(null)
    const primaryDriving = v === 'Yes'
    persistMultiVehicleCollisionPatch(policyRef, {
      multiVehiclePrimaryDriverDriving: primaryDriving,
      multiVehicleSecondaryOnPolicyDriving: null,
      multiVehicleListedDriverId: null,
      ...multiVehicleDriverIdentityResetPatch(),
    })
    if (!primaryDriving) {
      applyDriverPrefill(null, { force: true })
    }
    setLicenseNumber('')
    setLicenseExpiry('')
    setYearsLicensedInput('')
  }

  const chooseNoneOfListedDrivers = () => {
    setListedId(null)
    persistMultiVehicleCollisionPatch(policyRef, {
      multiVehicleSecondaryOnPolicyDriving: false,
      multiVehicleListedDriverId: null,
      ...multiVehicleDriverIdentityResetPatch(),
    })
    applyDriverPrefill(null, { force: true })
    setLicenseNumber('')
    setLicenseExpiry('')
    setYearsLicensedInput('')
    setPhase('driverDetails')
  }

  const canProceedFromQuestions =
    primaryAnswer === 'Yes' ||
    (primaryAnswer === 'No' && (secondaryDrivers.length === 0 || Boolean(listedId?.trim())))

  const {
    submitAttempted: questionsSubmitAttempted,
    showSubmitSummary: showQuestionsSubmitSummary,
    attemptSubmit: attemptQuestionsSubmit,
    clearSubmitAttempt: clearQuestionsSubmitAttempt,
  } = useClaimFormSubmitAttempt(canProceedFromQuestions)
  const primaryAnswerError = questionsSubmitAttempted && primaryAnswer === null
  const listedDriverError =
    questionsSubmitAttempted &&
    primaryAnswer === 'No' &&
    secondaryDrivers.length > 0 &&
    !listedId?.trim()

  const canProceedDriverDetails = useMemo(
    () =>
      isDriverDetailsFormComplete({
        firstName,
        lastName,
        dob,
        licenseNumber,
        licenseExpiry,
        yearsLicensedInput,
      }),
    [firstName, lastName, dob, licenseNumber, licenseExpiry, yearsLicensedInput]
  )

  const {
    submitAttempted: driverDetailsSubmitAttempted,
    showSubmitSummary: showDriverDetailsSubmitSummary,
    attemptSubmit: attemptDriverDetailsSubmit,
  } = useClaimFormSubmitAttempt(canProceedDriverDetails)

  const driverDetailsFieldErrors = useMemo(() => {
    if (!driverDetailsSubmitAttempted) {
      return {
        firstName: false,
        lastName: false,
        dob: false,
        licence: false,
        expiry: false,
        years: false,
      }
    }
    return {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      dob: !isValidDobField(dob),
      licence: !licenseNumber.trim(),
      expiry: !isValidExpiryField(licenseExpiry),
      years: !isValidYearsLicensedInput(yearsLicensedInput),
    }
  }, [
    driverDetailsSubmitAttempted,
    firstName,
    lastName,
    dob,
    licenseNumber,
    licenseExpiry,
    yearsLicensedInput,
  ])
  const driverNamesError = driverDetailsFieldErrors.firstName || driverDetailsFieldErrors.lastName

  const proceedFromQuestions = () => {
    attemptQuestionsSubmit(() => {
      if (primaryAnswer === 'Yes') {
        setPhase('driverDetails')
        return
      }
      if (secondaryDrivers.length === 0) {
        persistMultiVehicleCollisionPatch(policyRef, {
          multiVehicleSecondaryOnPolicyDriving: false,
          multiVehicleListedDriverId: null,
        })
        prefillRef.current = null
        setPhase('driverDetails')
        return
      }
      if (listedId?.trim()) {
        setPhase('driverDetails')
      }
    })
  }

  const submitDriverForm = () => {
    attemptDriverDetailsSubmit(() => {
    const fn = firstName.trim()
    const ln = lastName.trim()
    const dobT = dob.trim()
    const lic = licenseNumber.trim()
    const exp = licenseExpiry.trim()
    const yearsStr = yearsLicensedInput.trim()
    const yearsParsed = parseInt(yearsStr, 10)

    const d = readClaimDraft()
    let mode: 'primary' | 'listed' | 'manual'
    if (d?.multiVehiclePrimaryDriverDriving === true) {
      mode = 'primary'
    } else if (
      d?.multiVehicleSecondaryOnPolicyDriving === true &&
      Boolean(d.multiVehicleListedDriverId?.trim())
    ) {
      mode = 'listed'
    } else {
      mode = 'manual'
    }

    persistMultiVehicleCollisionPatch(policyRef, {
      multiVehicleDriverCaptureMode: mode,
      multiVehicleDriverFirstName: fn,
      multiVehicleDriverLastName: ln,
      multiVehicleDriverDobDdMmYyyy: dobT,
      multiVehicleDriverGender: gender === 'Male' || gender === 'Female' ? gender : null,
      multiVehicleLicenseNumber: lic,
      multiVehicleLicenseExpiryDdMmYyyy: exp,
      multiVehicleYearsLicensed: yearsParsed,
    })
    const draftAfterDriver = readClaimDraft()
    if (
      claimSubtype !== null &&
      isDriverStepSubtype(claimSubtype) &&
      isDriverDetailsGateSatisfiedForSubtype(draftAfterDriver, claimSubtype)
    ) {
      if (claimSubtype === SINGLE_VEHICLE_SUBTYPE) {
        navigateWithPolicy(buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype))
      } else {
        navigateWithPolicy(buildClaimOtherMultiVehicleOtherPartyHref(policyRef, typeInQuery, claimSubtype))
      }
    }
    })
  }

  const inputClass = CLAIM_FORM_CONTROL_CLASS

  useScrollToTopOnErrorSignal(
    formError ? 'multi-vehicle-driver-form' : showDriverDetailsSubmitSummary ? 'driver-details-validation' : null
  )

  if (claimSubtype === null || !isDriverStepSubtype(claimSubtype)) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={handleHeaderBack}
        title="Driver at collision"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={mvRailSummary}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(draft ?? undefined)}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          {phase === 'questions' ? (
            <div className="flex w-full flex-col gap-[28px]">
              {showQuestionsSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
              <section className="flex w-full flex-col gap-[16px]" aria-labelledby={primaryHeadingId}>
                <h2
                  id={primaryHeadingId}
                  className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
                >
                  Was {primaryName} driving at the time of the collision?
                </h2>
                <fieldset
                  className={`m-0 w-full min-w-0 border-0 p-0 ${primaryAnswerError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
                >
                  <legend className="sr-only">
                    Was the primary driver driving at the time of the collision? Choose Yes or No.
                  </legend>
                  <YesNoToggle
                    value={primaryAnswer ?? undefined}
                    onChange={(v) => persistPrimaryAnswer(v)}
                  />
                </fieldset>
                {primaryAnswerError ? (
                  <ClaimFormFieldError id={`${primaryHeadingId}-error`}>Choose Yes or No to continue.</ClaimFormFieldError>
                ) : null}
              </section>

              {primaryAnswer === 'No' && secondaryDrivers.length > 0 ? (
                <section className="flex w-full flex-col gap-[16px]" aria-labelledby={whoWasDrivingHeadingId}>
                  <h2
                    id={whoWasDrivingHeadingId}
                    className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
                  >
                    Who was driving?
                  </h2>
                  <p className="m-0 font-body text-xs leading-xs text-text-tertiary-600">
                    If they are not in the list below, choose <span className="font-semibold">None of the above</span>{' '}
                    to enter their details manually.
                  </p>
                  <div className="flex w-full flex-col gap-[8px]">
                    <label htmlFor={listedSelectId} className="font-body text-sm leading-sm text-text-tertiary-600">
                      Select who was driving
                    </label>
                    <select
                      id={listedSelectId}
                      value={listedId ?? ''}
                      onChange={(e) => {
                        clearQuestionsSubmitAttempt()
                        const v = e.target.value
                        setListedId(v || null)
                        persistMultiVehicleCollisionPatch(policyRef, {
                          multiVehicleSecondaryOnPolicyDriving: v ? true : null,
                          multiVehicleListedDriverId: v || null,
                          ...multiVehicleDriverIdentityResetPatch(),
                        })
                      }}
                      aria-invalid={listedDriverError}
                      aria-describedby={listedDriverError ? `${listedSelectId}-error` : undefined}
                      className={claimFormFieldClass(CLAIM_FORM_SELECT_CLASS, listedDriverError)}
                    >
                      <option value="">Choose…</option>
                      {secondaryDrivers.map((d) => {
                        const label = [d.firstName?.trim(), d.lastName?.trim()].filter(Boolean).join(' ')
                        return (
                          <option key={d.id} value={d.id}>
                            {label || 'Driver on your policy'}
                          </option>
                        )
                      })}
                    </select>
                    {listedDriverError ? (
                      <ClaimFormFieldError id={`${listedSelectId}-error`}>
                        Select who was driving, or choose None of the above.
                      </ClaimFormFieldError>
                    ) : null}
                  </div>
                  <SecondaryButton type="button" fullWidth size="lg" onClick={chooseNoneOfListedDrivers}>
                    None of the above
                  </SecondaryButton>
                </section>
              ) : null}
            </div>
          ) : null}

          {phase === 'driverDetails' ? (
            <section className="flex w-full flex-col gap-[16px]" aria-labelledby={detailsHeadingId}>
              <h2
                id={detailsHeadingId}
                className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
              >
                Driver details
              </h2>
              <p className="m-0 font-body text-xs leading-xs text-text-tertiary-600">
                We’ve filled in what we can from your policy. Add or correct details and complete the licence
                information.
              </p>
              {formError ? (
                <p role="alert" className="m-0 font-body text-xs leading-xs text-red-700">
                  {formError}
                </p>
              ) : null}
              {showDriverDetailsSubmitSummary ? (
                <ClaimFormSubmitSummaryAlert id={driverDetailsSummaryId} />
              ) : null}
              <div className="grid w-full gap-[12px] md:grid-cols-2">
                <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
                  First name
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    aria-invalid={driverDetailsFieldErrors.firstName || undefined}
                    className={claimFormFieldClass(inputClass, driverDetailsFieldErrors.firstName)}
                  />
                </label>
                <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
                  Last name
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    aria-invalid={driverDetailsFieldErrors.lastName || undefined}
                    className={claimFormFieldClass(inputClass, driverDetailsFieldErrors.lastName)}
                  />
                </label>
              </div>
              {driverNamesError ? (
                <ClaimFormFieldError id={driverNamesErrorId}>
                  Enter the driver’s first and last name.
                </ClaimFormFieldError>
              ) : null}
              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor={dobId}
                  className="font-body text-sm leading-sm text-text-tertiary-600"
                >
                  Date of birth (DD/MM/YYYY)
                </label>
                <DateInput
                  id={dobId}
                  value={dob}
                  onValueChange={setDob}
                  minDate={PORTAL_DATE_MIN}
                  maxDate={startOfTodayLocal()}
                  ariaDescribedBy={driverDetailsFieldErrors.dob ? driverDobErrorId : undefined}
                  ariaInvalid={driverDetailsFieldErrors.dob}
                  className={`${DATE_INPUT_FIELD_CLASS} ${
                    driverDetailsFieldErrors.dob ? CLAIM_FORM_FIELD_ERROR_RING_CLASS : ''
                  }`.trim()}
                />
                {driverDetailsFieldErrors.dob ? (
                  <ClaimFormFieldError id={driverDobErrorId}>
                    Enter a valid date of birth (DD/MM/YYYY).
                  </ClaimFormFieldError>
                ) : null}
              </div>
              <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
                Gender (optional)
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value as 'Male' | 'Female' | '')}
                  className={CLAIM_FORM_SELECT_CLASS}
                >
                  <option value="">Prefer not to say</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </label>
              <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
                Licence number
                <input
                  type="text"
                  autoComplete="off"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  aria-invalid={driverDetailsFieldErrors.licence || undefined}
                  aria-describedby={driverDetailsFieldErrors.licence ? driverLicenceErrorId : undefined}
                  className={claimFormFieldClass(inputClass, driverDetailsFieldErrors.licence)}
                />
              </label>
              {driverDetailsFieldErrors.licence ? (
                <ClaimFormFieldError id={driverLicenceErrorId}>
                  Enter the driver’s licence number.
                </ClaimFormFieldError>
              ) : null}
              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor={licenseExpiryId}
                  className="font-body text-sm leading-sm text-text-tertiary-600"
                >
                  Licence expiry (DD/MM/YYYY)
                </label>
                <DateInput
                  id={licenseExpiryId}
                  value={licenseExpiry}
                  onValueChange={setLicenseExpiry}
                  minDate={startOfTodayLocal()}
                  maxDate={licenceExpiryMaxDate()}
                  ariaDescribedBy={driverDetailsFieldErrors.expiry ? driverExpiryErrorId : undefined}
                  ariaInvalid={driverDetailsFieldErrors.expiry}
                  className={`${DATE_INPUT_FIELD_CLASS} ${
                    driverDetailsFieldErrors.expiry ? CLAIM_FORM_FIELD_ERROR_RING_CLASS : ''
                  }`.trim()}
                />
                {driverDetailsFieldErrors.expiry ? (
                  <ClaimFormFieldError id={driverExpiryErrorId}>
                    Enter a valid licence expiry date (DD/MM/YYYY), on or after today.
                  </ClaimFormFieldError>
                ) : null}
              </div>
              <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
                Years licensed
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  step={1}
                  placeholder="1–100"
                  value={yearsLicensedInput}
                  onChange={(e) => setYearsLicensedInput(e.target.value)}
                  aria-invalid={driverDetailsFieldErrors.years || undefined}
                  aria-describedby={driverDetailsFieldErrors.years ? driverYearsErrorId : undefined}
                  className={claimFormFieldClass(inputClass, driverDetailsFieldErrors.years)}
                />
              </label>
              {driverDetailsFieldErrors.years ? (
                <ClaimFormFieldError id={driverYearsErrorId}>
                  Enter years licensed as a whole number from 1 to 100.
                </ClaimFormFieldError>
              ) : null}
            </section>
          ) : null}
        </FormCard>

        {phase === 'questions' ? (
          <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
            <PrimaryButton
              type="button"
              onClick={proceedFromQuestions}
              size="lg"
              fullWidth
              rightIcon={
                <div className="size-5 text-text-white">
                  <ChevronRightIcon />
                </div>
              }
              aria-label="Next"
            >
              Next
            </PrimaryButton>
          </div>
        ) : null}

        {phase === 'driverDetails' ? (
          <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
            <PrimaryButton
              type="button"
              onClick={submitDriverForm}
              size="lg"
              fullWidth
              rightIcon={
                <div className="size-5 text-text-white">
                  <ChevronRightIcon />
                </div>
              }
              aria-label="Next"
            >
              Next
            </PrimaryButton>
          </div>
        ) : null}
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
